import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { Swiper as CarouselInstance } from 'swiper';
import { useSwiper } from 'swiper/react';
import { SwiperEvents } from 'swiper/types';

/**
 * Hook to obtain and manage CarouselInstance
 * */
export function useCarouselInstance(events?: (keyof SwiperEvents)[]) {
  const [carousel, setCarousel] = useState<CarouselInstance | null>(null);
  useRefreshInstance(carousel, events);

  return [carousel, setCarousel] as const;
}

/**
 * Hook to obtain CarouselInstance inside the Carousel component
 */
export function useCarousel(events?: (keyof SwiperEvents)[]) {
  const carousel = useSwiper();
  useRefreshInstance(carousel, events);

  return carousel;
}

/**
 * Internal hook that forces re-renders on events like slideChange, so the carousel.realIndex is correct
 * https://github.com/nolimits4web/swiper/issues/5577
 */
function useRefreshInstance(carousel: CarouselInstance, events?: (keyof SwiperEvents)[]) {
  // force re-render
  const [, setRerender] = useState({});
  useEffect(() => {
    if (carousel) {
      // subscribe to carousel events to trigger re-render
      (
        events ?? [
          'slideChange',
          'afterInit',
          'reachBeginning',
          'reachEnd',
          'autoplayStart',
          'autoplayStop',
          'lock',
          'unlock',
        ]
      ).forEach((evt: keyof SwiperEvents) => {
        carousel.on(evt, () => {
          setRerender({});
        });
      });
    }
  }, [carousel]);
  // once everything is rendered, make sure to re-render to have proper data (initialization)
  useLayoutEffect(() => {
    setRerender({});
  }, []);
}

/**
 * Manage peek effect in the Carousel by adding classes to the Carousel instance
 */
export function useCarouselPeekEffect(carousel: CarouselInstance) {
  const [peek, setPeek] = useState<'prev' | 'next' | 'reset' | null>(null);
  const peekTimeout = useRef<NodeJS.Timeout | null>(null);
  const [disabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (peek === 'reset') {
      carousel?.el?.classList.remove('peek-prev');
      carousel?.el?.classList.remove('peek-next');
      peekTimeout.current = setTimeout(
        () => {
          setPeek(null);
        },
        +(carousel?.el?.dataset?.peekspeed ?? 150),
      ); // matches transitionDuration in the Carousel
    } else if (peek) {
      if (!disabled) {
        // make sure prev/next slide is visible (loop)
        carousel?.slideReset(0);
        if (peek === 'prev') {
          carousel?.slidePrev(0);
          carousel?.slideNext(0);
        } else {
          carousel?.slideNext(0);
          carousel?.slidePrev(0);
        }
        carousel?.el?.classList.add('peeking');
        carousel?.el?.classList.add(`peek-${peek}`);
      }
    } else {
      carousel?.el?.classList.remove('peeking');
      carousel?.el?.classList.remove('peek-prev');
      carousel?.el?.classList.remove('peek-next');
    }

    return () => {
      if (peekTimeout.current) {
        clearTimeout(peekTimeout.current);
      }
    };
  }, [peek]);

  /**
   * Watch carousel instance
   */
  useEffect(() => {
    // once carousel is being dragged, reset and disable peek effect
    carousel?.on('touchStart', () => {
      setPeek('reset');
      setDisabled(true);
    });
    carousel?.on('touchEnd', () => {
      setPeek('reset');
      setDisabled(false);
    });
  }, [!!carousel]);

  return [peek, setPeek] as const;
}
